import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import PrintIcon from "@material-ui/icons/Print";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GetAppIcon from "@material-ui/icons/GetApp";
import axios from "axios";
import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import commaNumber from "comma-number";
import { API_URL } from "../../config";

let format = commaNumber.bindWith(",", ".");
const govtLogoSrc = `${process.env.PUBLIC_URL || ""}/images/bangladesh-govt-logo.svg`;

const printStyles = `
  .mushak-print-root {
    font-family: "SutonnyMJ", "Noto Serif Bengali", "SolaimanLipi", "Siyam Rupali", Arial, sans-serif;
    color: #111;
  }
  .mushak-print-root .mushak-en {
    font-family: "Times New Roman", Times, serif;
  }
  .mushak-print-root table {
    table-layout: fixed;
    width: 100%;
  }
  .mushak-print-root th, .mushak-print-root td {
    overflow-wrap: anywhere;
    word-break: break-word;
    font-size: 11px;
    white-space: normal;
  }
  @media print {
    @page {
      size: 224mm 275mm;
      margin: 8mm;
    }
    body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
    }
    .no-print {
      display: none !important;
    }
    .mushak-print-root {
      width: 100% !important;
      max-width: 208mm !important;
      min-height: auto !important;
      padding: 0 !important;
      box-sizing: border-box !important;
      margin: 0 auto !important;
      border: 0 !important;
    }
}
`;

const mushakTitleFontFamily = '"SutonnyMJ", "Noto Serif Bengali", "SolaimanLipi", "Siyam Rupali", Arial, sans-serif';

const mushakTitleStyle = {
  fontWeight: "bold",
  lineHeight: 1,
  fontFamily: mushakTitleFontFamily,
};
const emptyDetailSectionCellStyle = {
  borderTop: "0",
  borderBottom: "0",
  borderLeft: "0.8px solid #777",
  borderRight: "0.8px solid #777",
  padding: "0",
};
const detailBodyCellStyle = {
  borderTop: "0",
  borderBottom: "0",
  borderLeft: "0.8px solid #777",
  borderRight: "0.8px solid #777",
  padding: "3px",
  textAlign: "center",
  fontSize: "14px",
  fontWeight: "bold",
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const copyMeta = {
  1: { label: "প্রথম কপি" },
  2: { label: "দ্বিতীয় কপি" },
  3: { label: "তৃতীয় কপি" },
};
const val = (v, fallback = "-") => {
  if (v === null || v === undefined) return fallback;
  const s = `${v}`.trim();
  return s.length ? s : fallback;
};
const hasBanglaChars = (s) => /[\u0980-\u09FF]/.test(s);
const textWithLangFont = (v, fallback = "-") => {
  const s = val(v, fallback);
  if (s === fallback) return s;
  return hasBanglaChars(s) ? s : <span className="mushak-en">{s}</span>;
};

const getMushakProductLabel = (detail) => {
  const parts = [
    val(detail?.prod_name, ""),
    val(detail?.prod_cat_name, ""),
    val(detail?.prod_brand_name, ""),
  ].filter((part) => part && part !== "-");

  return parts.join("-");
};

const Mushak63Invoice = ({ authInfo, match, history }) => {
  const saleId = parseFloat(match?.params?.saleId);
  const [sale, setSale] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [copyType, setCopyType] = useState(2);
  const [vatOption, setVatOption] = useState("A");
  const printRef = useRef(null);

  useEffect(() => {
    if (!saleId || Number.isNaN(Number(saleId))) return;
    getSale();
    getInstitution();
  }, [saleId]);

  const getSale = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/get-sales-with-details`,
        { reqPayload: { saleId, from: "invoice" } },
        { headers: { "auth-token": authInfo.token } }
      );
      setSale(res?.data?.message?.[0] || null);
    } catch (_e) {
      setSale(null);
    }
  };

  const getInstitution = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/get-institution`, {
        headers: { "auth-token": authInfo.token },
      });
      setInstitution(res.data || null);
    } catch (_e) {
      setInstitution(null);
    }
  };

  const details = sale?.details || [];
  const minDetailRows = 8;
  const emptyDetailRows = Math.max(0, minDetailRows - details.length);
  const emptyDetailSectionHeight = emptyDetailRows * 36;
  const calculateVat = (amount) => {
    const numericAmount = num(amount);
    const vatRate = num(sale?.sale_vat_percent) || 10;
    switch (vatOption) {
      case "A":
        return (numericAmount * vatRate) / 100;
      case "B":
        return numericAmount - (numericAmount * 100) / (100 + vatRate);
      case "C":
        return 0;
      default:
        return (numericAmount * vatRate) / 100;
    }
  };
  const getBaseAmount = (amount) => {
    const numericAmount = num(amount);
    const vatRate = num(sale?.sale_vat_percent) || 10;
    if (vatOption === "B") {
      return (numericAmount * 100) / (100 + vatRate);
    }
    return numericAmount;
  };
  const getFinalPrice = (amount) => {
    const numericAmount = num(amount);
    switch (vatOption) {
      case "A":
        return numericAmount + calculateVat(numericAmount);
      case "B":
        return numericAmount;
      case "C":
        return numericAmount;
      default:
        return numericAmount;
    }
  };
  const subTotal = useMemo(
    () => details.reduce((sum, d) => sum + num(d.sale_prod_total), 0),
    [details]
  );
  const displaySubTotal = useMemo(
    () => details.reduce((sum, d) => sum + getBaseAmount(d.sale_prod_total), 0),
    [details, vatOption, sale]
  );
  const vatAmount = details.reduce((sum, d) => sum + calculateVat(d.sale_prod_total), 0);
  const grandTotal = getFinalPrice(subTotal);
  const totalQty = details.reduce((sum, d) => sum + num(d.sale_qty), 0);

  const sellerName = val(institution?.pro_name);
  const sellerAddress = val(
    institution?.pro_address ||
    institution?.pro_desc
  );
  const sellerBin =
    institution?.pro_bin ||
    institution?.pro_bin_number ||
    institution?.bin_number ||
    "-";

  const buyerIdValue =
    sale?.customer_bin_number ||
    sale?.customer_nid ||
    sale?.customer_trade_license ||
    "-";
  const deliveryDestination = val(
    sale?.delivery_address ||
    sale?.sale_delivery_address ||
    sale?.customer_shipping_address ||
    sale?.customer_address
  );
  const issueDate = sale?.sale_created_isodt
    ? moment(sale.sale_created_isodt).format("DD/MM/YYYY")
    : "";
  const issueTime = sale?.sale_created_isodt
    ? moment(sale.sale_created_isodt).format("h:mm A")
    : "";
  const vehicleInfo = val(
    sale?.sale_vehicle_no ||
    sale?.sale_transport_no ||
    sale?.sale_transport_name ||
    "BD"
  );

  const getPrintCanvas = async () => {
    if (!printRef.current) return null;

    const source = printRef.current;
    const holder = document.createElement("div");
    holder.style.position = "fixed";
    holder.style.left = "-20000px";
    holder.style.top = "0";
    holder.style.width = "1120px";
    holder.style.background = "#fff";
    holder.style.padding = "16px";
    holder.style.boxSizing = "border-box";
    holder.style.zIndex = "-1";

    const clone = source.cloneNode(true);
    clone.style.width = "100%";
    clone.style.maxWidth = "none";
    clone.style.margin = "0";
    clone.style.border = "0";
    clone.style.padding = "0";
    holder.appendChild(clone);
    document.body.appendChild(holder);

    try {
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: clone.scrollWidth,
        windowWidth: clone.scrollWidth,
      });
      return canvas;
    } finally {
      document.body.removeChild(holder);
    }
  };

  const buildPdf = async () => {
    const canvas = await getPrintCanvas();
    if (!canvas) return null;

    const pdf = new jsPDF("p", "mm", "a4");
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const drawW = pageW - margin * 2;
    const drawH = pageH - margin * 2;

    const imgData = canvas.toDataURL("image/png");
    const imgW = drawW;
    const imgH = (canvas.height * imgW) / canvas.width;

    if (imgH <= drawH) {
      pdf.addImage(imgData, "PNG", margin, margin, imgW, imgH);
    } else {
      pdf.addImage(imgData, "PNG", margin, margin, imgW, drawH);
    }

    return pdf;
  };

  const handleDownloadPdf = async () => {
    const pdf = await buildPdf();
    if (!pdf) return;
    pdf.save(`Mushak-6.3-${sale?.sale_invoice || saleId}-${copyMeta[copyType]?.label || "copy"}.pdf`);
  };

  const handlePrintClean = async (targetCopyType = copyType) => {
    setCopyType(targetCopyType);
    await new Promise((resolve) => setTimeout(resolve, 40));

    const pdf = await buildPdf();
    if (!pdf) return;

    const oldFrame = document.getElementById("mushak63-print-frame");
    if (oldFrame && oldFrame.parentNode) {
      oldFrame.parentNode.removeChild(oldFrame);
    }

    const blob = pdf.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.id = "mushak63-print-frame";
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      const cleanup = () => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.removeEventListener("afterprint", cleanup);
          }
        } catch (_e) {}
        setTimeout(() => {
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
          URL.revokeObjectURL(blobUrl);
        }, 500);
      };

      try {
        if (iframe.contentWindow) {
          iframe.contentWindow.addEventListener("afterprint", cleanup);
        }
      } catch (_e) {}

      setTimeout(() => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
        } catch (_e) {
          cleanup();
        }
      }, 350);

      // Fallback cleanup in case afterprint doesn't fire.
      setTimeout(() => {
        if (document.getElementById("mushak63-print-frame")) {
          cleanup();
        }
      }, 120000);
    };
  };

  return (
    <Grid container>
      <Grid item xs={12} sm={8} className={"invoice-section"}>
        <style>{printStyles}</style>

        <Paper style={{ padding: "8px" }}>
          <div className="no-print" style={{ marginBottom: "8px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                if (window.history.length > 1) history.goBack();
                else history.push("/sales/sales-invoice");
              }}
            >
              Back
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => handlePrintClean(1)}
            >
              ১ম কপি Print
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => handlePrintClean(2)}
            >
              ২য় কপি Print
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => handlePrintClean(3)}
            >
              ৩য় কপি Print
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<GetAppIcon />}
              onClick={handleDownloadPdf}
            >
              PDF
            </Button>
            </div>
            <div style={{ marginTop: "8px" }}>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={vatOption}
                  onChange={(event) => setVatOption(event.target.value)}
                >
                  <FormControlLabel value="A" control={<Radio size="small" />} label="Option A" />
                  <FormControlLabel value="B" control={<Radio size="small" />} label="Option B" />
                  <FormControlLabel value="C" control={<Radio size="small" />} label="Option C" />
                </RadioGroup>
              </FormControl>
            </div>
          </div>

          <div
            ref={printRef}
            className="mushak-print-root"
            style={{
              background: "#fff",
              border: "1px solid #ddd",
              padding: "22px",
              margin: "0 auto",
            }}
          >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "220px 1fr 220px",
                  width: "100%",
                  marginBottom: "0px",
                  alignItems: "center",
                  columnGap: "16px",
                }}
              >
              <div style={{ paddingTop: "2px", display: "flex", justifyContent: "flex-start" }}>
                <img
                  src={govtLogoSrc}
                  alt="Bangladesh Government Logo"
                  style={{ width: "99px", height: "99px", display: "block", marginLeft: "24px" }}
                />
              </div>
              <div style={{ textAlign: "center", fontSize: "14px", lineHeight: 1.25, marginBottom: "0px" }}>
                <div style={{ marginBottom: "2px" }}>গণপ্রজাতন্ত্রী বাংলাদেশ সরকার</div>
                <div style={{ marginBottom: "2px" }}>জাতীয় রাজস্ব বোর্ড</div>
                <div style={{ ...mushakTitleStyle, fontSize:"25px", marginTop: "2px", marginBottom: "2px" }}>কর চালানপত্র</div>
                <div style={{ marginTop: "2px", fontSize: "14px" }}>[বিধি ৪০ এর উপ-বিধি (১) এর দফা (গ) ও দফা (চ) দ্রষ্টব্য]</div>
              </div>
                <div style={{ paddingLeft: "6px", textAlign: "right", paddingTop: "2px" }}>
                  <div style={{ fontWeight: "bold", fontSize: "11px", marginBottom: "1px" }}>
                    {copyMeta[copyType]?.label || "দ্বিতীয় কপি"}
                  </div>
                  <div style={{...mushakTitleStyle, fontSize:"23px"}}>মূসক-৬.৩</div>
              </div>
            </div>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "11px",
                marginTop: "-6px",
                marginBottom: "10px",
                
              }}
            >
              <tbody>
                <tr>
                  <td style={{ width: "38%" }}></td>
                  <td style={{ width: "62%", padding: "2px 5px", lineHeight: 1.3 }}>
                    <div style={{ marginBottom: "3px", fontSize: "14px", fontWeight: 700 }}>
                      <strong>নিবন্ধিত ব্যক্তির নাম :</strong> {textWithLangFont(sellerName)}
                    </div>
                    <div style={{ marginBottom: "3px", fontSize: "14px", fontWeight: 700 }}>
                      <strong>নিবন্ধিত ব্যক্তির বিআইএন :</strong> {textWithLangFont(sellerBin)}
                    </div>
                    <div style={{ marginBottom: "3px", fontSize: "14px", fontWeight: 700 }}>
                      <strong>নিবন্ধিত ব্যক্তির ঠিকানা :</strong> {textWithLangFont(sellerAddress)}
                    </div>
                    <div style={{ fontSize: "14px", fontWeight: 700 }}>
                      <strong>চালানদাতা ইস্যুর ঠিকানা:</strong> {textWithLangFont(sellerAddress)}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "stretch",
                  fontSize: "11px",
                  marginTop: "0px",
                  marginBottom: "0px",
                  padding: "3px 6px",
                  gap: "0 5px",
                  lineHeight: 1.35,
                width: "100%",
                   
                }}
              >
                <div style={{ flex: "0 0 58%" }}>
                  <div style={{ marginBottom: "8px", fontSize: "14px", fontWeight: 700 }}>
                    <strong>ক্রেতার নাম :</strong> {textWithLangFont(sale?.customer_name)}
                  </div>
                  <div style={{ marginBottom: "8px", fontSize: "14px", fontWeight: 700 }}>
                    <strong>ক্রেতার বিআইএন :</strong> {textWithLangFont(buyerIdValue)}
                  </div>
                  <div style={{ marginBottom: "8px", fontSize: "14px", fontWeight: 700 }}>
                    <strong>ক্রেতার ঠিকানা :</strong> {textWithLangFont(sale?.customer_address)}
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: 700 }}>
                    <strong>সরবরাহের গন্তব্যস্থল :</strong> {textWithLangFont(deliveryDestination)}
                  </div>
                </div>
                <div
                  style={{
                    flex: "0 0 18%",
                    textAlign: "center",
                    marginLeft: "auto",
                    marginBottom: "-20px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "4px",
                  }}
              >
                <div style={{ marginBottom: "3px", fontSize: "14px", width: "100%" }}>
                  <strong>চালানপত্র নম্বর :</strong>&nbsp;
                </div>
                <div style={{ marginBottom: "3px", fontSize: "14px", fontWeight: 700, width: "100%" }}>
                  <strong>ইস্যুর তারিখ :</strong> {textWithLangFont(issueDate)}
                </div>
                <div style={{ fontSize: "14px", fontWeight: 700, width: "100%" }}>
                  <strong>ইস্যুর সময় :</strong> {textWithLangFont(issueTime)}
                </div>
                
              </div>
            </div>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "11px",
                marginTop: "0px",
                marginBottom: "6px",
                position: "relative",
                top: "-2px",
              }}
            >
              <tbody>
                <tr>
                  <td style={{ padding: "3px 6px" }}>
                    <span style={{ fontSize: "14px", fontWeight: 700 }}>
                      <strong>যানবাহনের প্রকৃতি ও নম্বর:</strong> {textWithLangFont(vehicleInfo)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", marginBottom: "6px" }}>
              <colgroup>
                <col style={{ width: "3%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "4.8%" }} />
                <col style={{ width: "4%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "4%" }} />
                <col style={{ width: "3%" }} />
                <col style={{ width: "4%" }} />
                <col style={{ width: "4%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "5%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>ক্রমিক</th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>
                    <div style={{ textAlign: "center", lineHeight: 1.15 }}>
                      পণ্য বা সেবার বর্ণনা
                      (প্রযোজ্য ক্ষেত্রে ব্র্যান্ড নামসহ)
                    </div>
                  </th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>সরবরাহের একক</th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>পরিমাণ</th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>একক মূল্য (টাকায়)</th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>মোট মূল্য (টাকায়)</th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>সম্পূরক শুল্কের হার</th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>সম্পূরক শুল্কের পরিমাণ (টাকায়)</th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>মূল্য সংযোজন করের হার/সুনির্দিষ্ট কর</th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>
                    মূল্য সংযোজন কর/সুনির্দিষ্ট কর এর পরিমাণ (টাকায়)
                  </th>
                  <th style={{ border: "0.8px solid #777", padding: "6px" }}>সকল প্রকার শুল্ক ও করসহ মূল্য</th>
                </tr>
                <tr>
                  {["(১)", "(২)", "(৩)", "(৪)", "(৫)", "(৬)", "(৭)", "(৮)", "(৯)", "(১০)", "(১১)"].map((c) => (
                    <th key={c} style={{ border: "0.8px solid #777", padding: "1px 2px", fontWeight: "bold" }}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {details.length === 0 && (
                  <tr>
                    <td colSpan={11} style={{ border: "0.8px solid #777", padding: "8px", textAlign: "center" }}>
                      কোনও পণ্য তথ্য পাওয়া যায়নি
                    </td>
                  </tr>
                )}
                {details.map((d, i) => {
                  const grossLineTotal = num(d.sale_prod_total);
                  const lineSub = getBaseAmount(grossLineTotal);
                  const lineVat = calculateVat(grossLineTotal);
                  const lineTotal = getFinalPrice(grossLineTotal);
                  const baseUnitRate = num(d.sale_qty) ? lineSub / num(d.sale_qty) : 0;
                  return (
                    <tr key={i}>
                      <td style={detailBodyCellStyle}>{textWithLangFont(i + 1)}</td>
                      <td style={detailBodyCellStyle}>{textWithLangFont(getMushakProductLabel(d) || d.prod_name)}</td>
                      <td style={detailBodyCellStyle}>{textWithLangFont(d.prod_unit_name)}</td>
                      <td style={detailBodyCellStyle}>{textWithLangFont(num(d.sale_qty))}</td>
                      <td style={detailBodyCellStyle}>{textWithLangFont(format((vatOption === "B" ? baseUnitRate : num(d.sale_rate)).toFixed(2)))}</td>
                      <td style={detailBodyCellStyle}>{textWithLangFont(format(lineSub.toFixed(2)))}</td>
                      <td style={detailBodyCellStyle}>-</td>
                      <td style={detailBodyCellStyle}>{textWithLangFont("0.00")}</td>
                      <td style={detailBodyCellStyle}>
                        {textWithLangFont(
                          vatOption === "C"
                            ? "0.00%"
                            : `${(num(sale?.sale_vat_percent) || 10).toFixed(2)}%`
                        )}
                      </td>
                      <td style={detailBodyCellStyle}>{textWithLangFont(format(lineVat.toFixed(2)))}</td>
                      <td style={detailBodyCellStyle}>{textWithLangFont(format(lineTotal.toFixed(2)))}</td>
                    </tr>
                  );
                })}
                {emptyDetailSectionHeight > 0 && (
                  <tr>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                    <td style={{ ...emptyDetailSectionCellStyle, height: `${emptyDetailSectionHeight}px` }}></td>
                  </tr>
                )}
                <tr>
                  <td style={{ border: "0.8px solid #777", padding: "4px" }}></td>
                  <td style={{ border: "0.8px solid #777", padding: "4px" }}></td>
                  <td style={{ border: "0.8px solid #777", padding: "4px" }}></td>
                  <td style={{ border: "0.8px solid #777", padding: "4px" }}></td>
                  <td style={{ border: "0.8px solid #777", padding: "4px", textAlign: "right", fontWeight: "bold", fontSize: "14px" }}>
                    সর্বমোট
                  </td>
                  <td style={{ border: "0.8px solid #777", padding: "4px", textAlign: "center", fontWeight: "bold", fontSize: "14px" }}>
                    {textWithLangFont(format(displaySubTotal.toFixed(2)))}
                  </td>
                  <td style={{ border: "0.8px solid #777", padding: "4px" }}></td>
                  <td style={{ border: "0.8px solid #777", padding: "4px" }}></td>
                  <td style={{ border: "0.8px solid #777", padding: "4px" }}></td>
                  <td style={{ border: "0.8px solid #777", padding: "4px", textAlign: "center", fontWeight: "bold", fontSize: "14px" }}>
                    {textWithLangFont(format(vatAmount.toFixed(2)))}
                  </td>
                  <td style={{ border: "0.8px solid #777", padding: "4px", textAlign: "center", fontWeight: "bold", fontSize: "14px" }}>
                    {textWithLangFont(format(grandTotal.toFixed(2)))}
                  </td>
                </tr>
              </tbody>
            </table>

            <table style={{ width: "100%", fontSize: "12px" }}>
              <tbody>
                <tr>
                  <td style={{ width: "50%", textAlign: "left", paddingTop: "18px" }}>
                    <div style={{ fontSize: "14px", fontWeight: 700 }}>
                      <strong>দায়িত্বপ্রাপ্ত ব্যক্তির নাম:</strong>
                    </div>
                    <div style={{ marginTop: "9px", fontSize: "14px", fontWeight: 700 }}><strong>পদবি :</strong></div>
                    <div style={{ paddingTop: "12px", width: "165px", borderBottom: "0.8px solid #666" }}>
                      &nbsp;
                    </div>
                    <div style={{ marginTop: "2px", fontSize: "14px", fontWeight: 700 }}>স্বাক্ষর</div>
                  </td>
                </tr>
              </tbody>
            </table>

            <table style={{ width: "100%", marginTop: "10px", borderCollapse: "collapse", fontSize: "12px" }}>
              <tbody>
                <tr>
                  <td style={{ border: "0.8px solid #666", padding: "5px 6px", width: "320px", fontSize: "14px", fontWeight: 700 }}>
                    <strong>"সকল প্রকার কর ব্যতীত মূল্য"</strong> {textWithLangFont(format(subTotal.toFixed(2)))}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            {/* Legacy simple sign block removed in favor of form-like signature fields */}
            {/* <table style={{ width: "100%", fontSize: "13px" }}>
              <tbody>
                <tr>
                  <td style={{ width: "50%", textAlign: "center", paddingTop: "30px" }}>
                    ____________________
                    <br />
                    দায়িত্বপ্রাপ্ত ব্যক্তির স্বাক্ষর
                  </td>
                  <td style={{ width: "50%", textAlign: "center", paddingTop: "30px" }}>
                    ____________________
                    <br />
                    কোম্পানির সিল
                  </td>
                </tr>
              </tbody>
            </table> */}
          </div>
        </Paper>
      </Grid>
    </Grid>
  );
};

const mapStateToPops = (state) => {
  return {
    authInfo: state.authInfoReducer,
  };
};

export default connect(mapStateToPops, {})(Mushak63Invoice);
